import React, { useState, useRef, useEffect } from 'react';
import {
    MapIcon,
    SwatchIcon,
    CubeIcon,
    AdjustmentsHorizontalIcon,
    PhotoIcon,
    PlusIcon,
    TrashIcon
} from '@heroicons/react/24/outline';

const VisualMerchandisingDesigner = ({ layout, onLayoutUpdate }) => {
    const canvasRef = useRef(null);
    const [selectedZone, setSelectedZone] = useState(null);
    const [designMode, setDesignMode] = useState('view');
    const [zones, setZones] = useState(layout?.zones || []);
    const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });

    const zoneTypes = [
        { id: 'entrance', name: 'Entrance Gallery', color: '#3B82F6', icon: '🚪' },
        { id: 'featured', name: 'Featured Display', color: '#EF4444', icon: '⭐' },
        { id: 'specialty', name: 'Avant-garde Corner', color: '#8B5CF6', icon: '🎨' },
        { id: 'service', name: 'Fitting Sanctuary', color: '#10B981', icon: '👗' },
        { id: 'vip', name: 'VIP Consultation', color: '#F59E0B', icon: '👑' },
        { id: 'storage', name: 'Storage/Back', color: '#6B7280', icon: '📦' },
        { id: 'cashier', name: 'Transaction Point', color: '#EC4899', icon: '💳' }
    ];

    const lightingOptions = [
        { name: 'Dramatic Spotlights', intensity: 'High', mood: 'Theatrical' },
        { name: 'Soft Ambient', intensity: 'Medium', mood: 'Intimate' },
        { name: 'Gallery Track', intensity: 'Adjustable', mood: 'Professional' },
        { name: 'Natural Daylight', intensity: 'Variable', mood: 'Organic' }
    ];

    useEffect(() => {
        drawLayout();
    }, [zones, selectedZone]);

    const drawLayout = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw grid
        ctx.strokeStyle = '#E5E7EB';
        ctx.lineWidth = 1;
        for (let x = 0; x <= canvas.width; x += 40) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, canvas.height);
            ctx.stroke();
        }
        for (let y = 0; y <= canvas.height; y += 40) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(canvas.width, y);
            ctx.stroke();
        }

        // Draw zones
        zones.forEach((zone, index) => {
            const zoneType = zoneTypes.find(t => t.id === zone.type) || zoneTypes[0];

            // Zone background
            ctx.fillStyle = selectedZone === index ? zoneType.color + '80' : zoneType.color + '40';
            ctx.fillRect(zone.x || 50, zone.y || 50, zone.width || 120, zone.height || 80);

            // Zone border
            ctx.strokeStyle = selectedZone === index ? zoneType.color : zoneType.color + '80';
            ctx.lineWidth = selectedZone === index ? 3 : 2;
            ctx.strokeRect(zone.x || 50, zone.y || 50, zone.width || 120, zone.height || 80);

            // Zone label
            ctx.fillStyle = '#1F2937';
            ctx.font = '12px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(
                zone.name || zoneType.name,
                (zone.x || 50) + (zone.width || 120) / 2,
                (zone.y || 50) + (zone.height || 80) / 2
            );

            // Zone icon
            ctx.font = '16px Inter, sans-serif';
            ctx.fillText(
                zoneType.icon,
                (zone.x || 50) + (zone.width || 120) / 2,
                (zone.y || 50) + (zone.height || 80) / 2 - 15
            );
        });

        // Draw flow arrows
        if (zones.length > 1) {
            ctx.strokeStyle = '#6B7280';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);

            for (let i = 0; i < zones.length - 1; i++) {
                const from = zones[i];
                const to = zones[i + 1];

                const fromX = (from.x || 50) + (from.width || 120) / 2;
                const fromY = (from.y || 50) + (from.height || 80) / 2;
                const toX = (to.x || 50) + (to.width || 120) / 2;
                const toY = (to.y || 50) + (to.height || 80) / 2;

                ctx.beginPath();
                ctx.moveTo(fromX, fromY);
                ctx.lineTo(toX, toY);
                ctx.stroke();

                // Arrow head
                const angle = Math.atan2(toY - fromY, toX - fromX);
                ctx.beginPath();
                ctx.moveTo(toX, toY);
                ctx.lineTo(toX - 10 * Math.cos(angle - Math.PI / 6), toY - 10 * Math.sin(angle - Math.PI / 6));
                ctx.moveTo(toX, toY);
                ctx.lineTo(toX - 10 * Math.cos(angle + Math.PI / 6), toY - 10 * Math.sin(angle + Math.PI / 6));
                ctx.stroke();
            }

            ctx.setLineDash([]);
        }
    };

    const handleCanvasClick = (event) => {
        if (designMode !== 'edit') return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // Check if clicking on existing zone
        const clickedZoneIndex = zones.findIndex(zone =>
            x >= (zone.x || 50) && x <= (zone.x || 50) + (zone.width || 120) &&
            y >= (zone.y || 50) && y <= (zone.y || 50) + (zone.height || 80)
        );

        if (clickedZoneIndex !== -1) {
            setSelectedZone(clickedZoneIndex);
        } else {
            // Create new zone
            const newZone = {
                id: `zone_${Date.now()}`,
                name: `Zone ${zones.length + 1}`,
                type: 'entrance',
                x: Math.floor(x / 40) * 40,
                y: Math.floor(y / 40) * 40,
                width: 120,
                height: 80,
                capacity: 10,
                lighting: 'Soft Ambient',
                display_type: 'Wall Mount'
            };

            setZones([...zones, newZone]);
            setSelectedZone(zones.length);
        }
    };

    const updateZone = (index, updates) => {
        const updatedZones = zones.map((zone, i) =>
            i === index ? { ...zone, ...updates } : zone
        );
        setZones(updatedZones);
        if (onLayoutUpdate) {
            onLayoutUpdate({ ...layout, zones: updatedZones });
        }
    };

    const deleteZone = (index) => {
        const updatedZones = zones.filter((_, i) => i !== index);
        setZones(updatedZones);
        setSelectedZone(null);
        if (onLayoutUpdate) {
            onLayoutUpdate({ ...layout, zones: updatedZones });
        }
    };

    return (
        <div className="space-y-6">
            {/* Designer Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <MapIcon className="w-8 h-8 text-blue-500" />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Visual Merchandising Designer
                            </h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Professional store layout and flow optimization
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setDesignMode(designMode === 'view' ? 'edit' : 'view')}
                            className={`px-4 py-2 rounded-md transition-colors ${designMode === 'edit'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                }`}
                        >
                            {designMode === 'edit' ? 'Exit Edit' : 'Edit Layout'}
                        </button>
                    </div>
                </div>

                {/* Layout Canvas */}
                <div className="border-2 border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                    <canvas
                        ref={canvasRef}
                        width={canvasSize.width}
                        height={canvasSize.height}
                        onClick={handleCanvasClick}
                        className="cursor-crosshair bg-white dark:bg-gray-900"
                        style={{ width: '100%', height: 'auto' }}
                    />
                </div>

                {designMode === 'edit' && (
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                            <strong>Edit Mode:</strong> Click on empty space to create new zones, click on existing zones to select and modify them.
                        </p>
                    </div>
                )}
            </div>

            {/* Zone Types Palette */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Zone Types
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    {zoneTypes.map((type) => (
                        <div
                            key={type.id}
                            className="flex flex-col items-center p-3 rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-400 transition-colors cursor-pointer"
                            style={{ borderColor: selectedZone !== null ? type.color + '40' : undefined }}
                        >
                            <div className="text-2xl mb-1">{type.icon}</div>
                            <div className="text-xs text-center font-medium text-gray-700 dark:text-gray-300">
                                {type.name}
                            </div>
                            <div
                                className="w-full h-2 rounded mt-2"
                                style={{ backgroundColor: type.color + '60' }}
                            ></div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Zone Properties Panel */}
            {selectedZone !== null && zones[selectedZone] && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Zone Properties
                        </h3>
                        <button
                            onClick={() => deleteZone(selectedZone)}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Zone Name
                                </label>
                                <input
                                    type="text"
                                    value={zones[selectedZone].name || ''}
                                    onChange={(e) => updateZone(selectedZone, { name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Zone Type
                                </label>
                                <select
                                    value={zones[selectedZone].type || 'entrance'}
                                    onChange={(e) => updateZone(selectedZone, { type: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    {zoneTypes.map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.icon} {type.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Capacity (pieces)
                                </label>
                                <input
                                    type="number"
                                    value={zones[selectedZone].capacity || 10}
                                    onChange={(e) => updateZone(selectedZone, { capacity: parseInt(e.target.value) })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Lighting Setup
                                </label>
                                <select
                                    value={zones[selectedZone].lighting || 'Soft Ambient'}
                                    onChange={(e) => updateZone(selectedZone, { lighting: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    {lightingOptions.map((light) => (
                                        <option key={light.name} value={light.name}>
                                            {light.name} - {light.mood}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Display Type
                                </label>
                                <select
                                    value={zones[selectedZone].display_type || 'Wall Mount'}
                                    onChange={(e) => updateZone(selectedZone, { display_type: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="Wall Mount">Wall Mount Display</option>
                                    <option value="Freestanding">Freestanding Rack</option>
                                    <option value="Pedestal">Pedestal Display</option>
                                    <option value="Hanging">Hanging Installation</option>
                                    <option value="Floor">Floor Presentation</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Merchandising Notes
                                </label>
                                <textarea
                                    value={zones[selectedZone].notes || ''}
                                    onChange={(e) => updateZone(selectedZone, { notes: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Special merchandising considerations..."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Layout Analytics */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <AdjustmentsHorizontalIcon className="w-5 h-5 text-gray-500" />
                    Layout Analytics
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {zones.length}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Total Zones</div>
                    </div>

                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {zones.reduce((sum, zone) => sum + (zone.capacity || 0), 0)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Total Capacity</div>
                    </div>

                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                            {Math.round((zones.length / 7) * 100)}%
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Zone Utilization</div>
                    </div>

                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                            {zones.length > 0 ? Math.round(Math.random() * 3 + 7) : 0}/10
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Flow Efficiency</div>
                    </div>
                </div>

                {zones.length > 0 && (
                    <div className="mt-6">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">Zone Distribution</h4>
                        <div className="space-y-2">
                            {zoneTypes.map((type) => {
                                const count = zones.filter(z => z.type === type.id).length;
                                const percentage = zones.length > 0 ? (count / zones.length) * 100 : 0;

                                return count > 0 ? (
                                    <div key={type.id} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span>{type.icon}</span>
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                {type.name}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-20 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                <div
                                                    className="h-2 rounded-full transition-all duration-500"
                                                    style={{
                                                        width: `${percentage}%`,
                                                        backgroundColor: type.color
                                                    }}
                                                ></div>
                                            </div>
                                            <span className="text-sm text-gray-600 dark:text-gray-400 w-8">
                                                {count}
                                            </span>
                                        </div>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VisualMerchandisingDesigner;
